import { useState } from "react";
import axios from "axios";
import { X, CheckCircle2, Loader2, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { JerseyWithLogo } from "@/components/TeamGrid";
import { SIZES, JERSEY_PRICE, JERSEY_SHIPPING } from "@/cleanteamData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => `$${Number(n).toFixed(2)}`;

export default function JerseyModal({ team, onClose }) {
  const [size, setSize] = useState(null);
  const [qty, setQty] = useState(1);
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", notes: "" });
  const [sending, setSending] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  if (!team) return null;

  const subtotal = JERSEY_PRICE * qty;
  const total = subtotal + JERSEY_SHIPPING;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const inputCls = "w-full rounded-xl border border-white/15 bg-[#07090E] px-4 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#39FF14]/40";

  const submit = async (e) => {
    e.preventDefault();
    if (!size) { toast.error("Pick a size first"); return; }
    setSending(true);
    try {
      const r = await axios.post(`${API}/jerseys/order`, {
        team_id: team.id, team_name: team.name, size, quantity: qty, ...form,
      });
      setConfirmation(r.data);
      toast.success("Order reserved — complete payment to lock it in");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map((x) => x.msg).join(", ") : (typeof d === "string" ? d : "Something went wrong. Please try again."));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="jersey-modal">
      <button className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="jersey-modal-backdrop" aria-label="Close" />
      <div className="relative bg-[#0F141F] border border-white/10 rounded-3xl max-w-md w-full p-6 sm:p-8 shadow-2xl max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} data-testid="jersey-modal-close"
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/5 hover:bg-white/15 flex items-center justify-center transition-colors">
          <X size={16} className="text-white/70" />
        </button>

        {confirmation ? (
          <div className="text-center py-4" data-testid="ct-confirmation">
            <CheckCircle2 size={48} className="text-[#39FF14] mx-auto" />
            <h3 className="font-['Barlow_Condensed',sans-serif] text-3xl font-extrabold uppercase text-white mt-4">Order Locked In</h3>
            <p className="text-sm text-white/50 mt-2">Confirmation</p>
            <p className="text-2xl font-black tracking-widest text-[#39FF14] mt-1" data-testid="ct-confirmation-number">{confirmation.confirmation}</p>
            <p className="text-sm text-white/60 mt-3">
              {team.name} · Size {confirmation.size} · Qty {confirmation.quantity} — total <strong>{fmt(confirmation.total)}</strong> (shipping included).
            </p>
            {confirmation.checkout_url && (
              <a href={confirmation.checkout_url} target="_blank" rel="noopener noreferrer" data-testid="ct-checkout-btn"
                className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#39FF14] text-[#07090E] font-bold uppercase tracking-wider py-3.5 px-8 rounded-full hover:bg-[#5CFF3D] transition-colors">
                Pay Now — Secure Checkout
              </a>
            )}
            {!confirmation.checkout_url && (
              <p className="mt-4 text-xs text-[#FFD700] bg-[#FFD700]/10 rounded-xl px-4 py-2.5" data-testid="ct-checkout-fallback">
                Online checkout is busy — we'll email your secure payment link shortly.
              </p>
            )}
            <button onClick={onClose} data-testid="ct-done-btn"
              className="mt-3 w-full border border-white/15 text-white/70 font-semibold py-3 rounded-full hover:bg-white/5 transition-colors">Done</button>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="jersey-order-form">
            <div className="flex items-center gap-4">
              <span className="w-20 h-20 rounded-2xl flex items-center justify-center p-2 shrink-0"
                style={{ background: `radial-gradient(circle at 50% 35%, ${team.primary}44, transparent 75%)`, border: `1px solid ${team.secondary}55` }}>
                <JerseyWithLogo team={team} className="w-14 h-14" />
              </span>
              <div>
                <h3 className="font-['Barlow_Condensed',sans-serif] text-2xl font-extrabold uppercase text-white leading-tight" data-testid="jersey-modal-team">{team.name}</h3>
                <p className="text-sm text-white/50">Stitched Jersey · {fmt(JERSEY_PRICE)} each</p>
              </div>
            </div>

            <div className="mt-6">
              <span className="text-[11px] font-bold uppercase tracking-widest text-white/50">Size</span>
              <div className="mt-2 flex gap-2" data-testid="size-selector">
                {SIZES.map((s) => (
                  <button key={s} type="button" onClick={() => setSize(s)} data-testid={`size-btn-${s.toLowerCase()}`}
                    className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all duration-200 ${
                      size === s ? "bg-[#39FF14] text-[#07090E] scale-105" : "border border-white/15 text-white/60 hover:border-[#39FF14]/50 hover:text-white"
                    }`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-5 flex items-center justify-between">
              <span className="text-[11px] font-bold uppercase tracking-widest text-white/50">Quantity</span>
              <div className="flex items-center gap-3" data-testid="qty-stepper">
                <button type="button" onClick={() => setQty(Math.max(1, qty - 1))} data-testid="qty-minus"
                  className="w-8 h-8 rounded-full border border-white/15 text-white/70 flex items-center justify-center hover:border-[#39FF14]/50"><Minus size={14} /></button>
                <span className="font-['Barlow_Condensed',sans-serif] text-xl font-bold text-white w-6 text-center" data-testid="qty-value">{qty}</span>
                <button type="button" onClick={() => setQty(Math.min(10, qty + 1))} data-testid="qty-plus"
                  className="w-8 h-8 rounded-full border border-white/15 text-white/70 flex items-center justify-center hover:border-[#39FF14]/50"><Plus size={14} /></button>
              </div>
            </div>

            <div className="mt-5 rounded-xl bg-[#07090E] border border-white/10 p-3.5 space-y-1.5 text-sm text-white/60" data-testid="jersey-price-summary">
              <div className="flex justify-between"><span>{qty} × {team.abbr} jersey</span><span>{fmt(subtotal)}</span></div>
              <div className="flex justify-between"><span>Fast US Shipping</span><span>{fmt(JERSEY_SHIPPING)}</span></div>
              <div className="flex justify-between pt-1.5 border-t border-white/10 text-white font-bold"><span>Total</span><span data-testid="jersey-total">{fmt(total)}</span></div>
            </div>

            <div className="mt-5 space-y-3">
              <input required placeholder="Full name" value={form.name} onChange={set("name")} className={inputCls} data-testid="ct-name-input" />
              <input required type="email" placeholder="Email address" value={form.email} onChange={set("email")} className={inputCls} data-testid="ct-email-input" />
              <input placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="ct-phone-input" />
              <input required placeholder="Shipping address" value={form.address} onChange={set("address")} className={inputCls} data-testid="ct-address-input" />
            </div>

            <button type="submit" disabled={sending} data-testid="ct-submit-order-btn"
              className="mt-6 w-full bg-[#39FF14] text-[#07090E] font-bold uppercase tracking-wider py-3.5 rounded-full hover:bg-[#5CFF3D] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
              {sending ? <><Loader2 size={16} className="animate-spin" /> Reserving...</> : "Lock In My Jersey"}
            </button>
            <p className="mt-3 text-center text-[11px] text-white/40">Dispatched in 2 business days · Easy 30-day returns</p>
          </form>
        )}
      </div>
    </div>
  );
}
